import * as t from "io-ts";
import { fetchJSON } from "./fetchJSON";
import { API_BASE_URL } from "../config";
const CodeWithLabel = t.interface({
    id: t.string,
    label: t.string
}, "CodeWithLabel");
const GeoDimension = t.interface({
    id: t.string,
    label: t.string,
    type: t.literal("geo"),
    domain: t.array(CodeWithLabel)
}, "GeoDimension");
const NominalDimension = t.interface({
    id: t.string,
    label: t.string,
    type: t.literal("nominal"),
    domain: t.array(CodeWithLabel)
}, "NominalDimension");
const ContinuousDimension = t.interface({
    id: t.string,
    label: t.string,
    type: t.literal("continuous"),
    domain: t.array(t.string) // TODO Refine for dates/numbers
}, "ContinuousDimension");
const Dimension = t.taggedUnion("type", [GeoDimension, NominalDimension, ContinuousDimension], "Dimension");
const Facet = t.intersection([
    t.interface({
        id: t.string,
        label: t.string
    }),
    t.dictionary(t.string, t.string)
], "Facet");
const SourceWithUrl = t.interface({
    text: t.string,
    url: t.string
}, "SourceWithUrl");
const GlossarItemWithUrl = t.interface({
    text: t.string,
    url: t.string
}, "GlossarItemWithUrl");
const UpdateHistoryItem = t.interface({
    text: t.string,
    date: t.string
}, "UpdateHistoryItem");
const UpdateHistory = t.interface({
    items: t.array(UpdateHistoryItem),
    url: t.string
}, "UpdateHistory");
const FootnotesItem = t.interface({
    id: t.string,
    label: t.string
}, "FootnotesItem");
/* const Footnotes = t.interface(
  {
    items: t.array(FootnotesItem),
  },
  "Footnotes"
); */
const Licence = t.interface({
    text: t.string,
    url: t.string
}, "Licence");
const Unit = t.union([t.literal("Percent"), t.any]);
const Dataset = t.interface({
    id: t.string,
    dataURI: t.string,
    label: t.string,
    measure: Dimension,
    dimensions: t.array(Dimension),
    facets: t.array(Facet),
    unit: Unit,
    downloadUrl: t.string,
    sources: t.array(SourceWithUrl),
    glossarItems: t.array(GlossarItemWithUrl),
    updateHistory: UpdateHistory,
    footnotes: t.array(FootnotesItem),
    collectingProcess: t.string,
    licence: Licence
}, "Dataset");
/**
 * Transform API response into a more type-able structure.
 * We're using (d: any) -> any because we validate the result.
 *
 * **BEWARE**: There's no defensive coding in here. If a property doesn't
 * exist in the response, the whole transformation will blow up! This could
 * probably be done in a safer way (everything as Maybe?) but for now, errors
 * have to be caught somewhere else.
 */
const transformDataSet = (dataset) => {
    // 1. Extract the default slice
    const defaultSlice = dataset.slice.find((d) => d["@type"].indexOf("DefaultSlice") > -1);
    if (!defaultSlice) {
        throw Error(`No default slice found in dataset`);
    }
    // 2. Get the label and measure of the dataset
    const label = defaultSlice.label;
    // 3. Get dimensions and their domain – if not geo/nominal, then add something invalid
    const dimensionsWithMeasure = (Array.isArray(defaultSlice.shapesGraph.property)
        ? defaultSlice.shapesGraph.property
        : [defaultSlice.shapesGraph.property]).map((shp) => {
        const dimensionType = shp.in && shp.notation === "RAUM"
            ? "geo"
            : shp.in && shp.in.length
                ? "nominal"
                : shp.minInclusive && shp.maxInclusive
                    ? "continuous"
                    : "UNKNOWN_DIMENSION_TYPE";
        return {
            id: shp.notation,
            label: shp.label,
            isMeasure: /measure/.test(shp.seeAlso),
            type: dimensionType,
            domain: dimensionType === "nominal" || dimensionType === "geo"
                ? shp.in.map((d) => ({
                    id: d["@id"].replace("ssz-code:", ""),
                    label: d.label
                }))
                : dimensionType === "continuous"
                    ? [shp.minInclusive["@value"], shp.maxInclusive["@value"]] // TODO parse @type correctly
                    : "UNKNOWN_DIMENSION_DOMAIN",
            scopeNote: shp.scopeNote
        };
    });
    const dimensions = dimensionsWithMeasure.filter((d) => !d.isMeasure);
    const measure = dimensionsWithMeasure.find((d) => d.isMeasure);
    if (!measure) {
        throw Error(`No measure found in dataset`);
    }
    // Get facets, i.e. all slices except the default slice
    const facets = dataset.slice
        .filter((d) => d["@type"] === "Slice")
        .map((slice) => {
        // Extract the facet values for each dimension
        const facetValues = dimensions.reduce((values, dim) => {
            const dimKey = `d${dim.id}`;
            // dRAUM, dZEIT and d{measure} are never defined on slices
            if (dimKey in slice) {
                // We just use the @id here *for now* because labels may or may not be present
                // all labels *should* be in the dimension's domain though
                // TODO: decide if we resolve labels here
                values[`d${dim.id}`] = slice[`d${dim.id}`]["@id"].replace("ssz-code:", "");
            }
            return values;
        }, {});
        return {
            id: [measure.id, ...Object.values(facetValues).sort()].join("/"),
            label: Array.isArray(slice.label) ? slice.label[0] : slice.label,
            ...facetValues
        };
    });
    const unit = dataset.unitMeasure.label;
    /**
     * What follows is metadata added to the API result
     */
    const downloadUrl = "#";
    let sources = [
        {
            text: "Es sind keine Quellen-Einträge vorhanden.",
            url: "#"
        }
    ];
    dimensions.forEach((dim) => {
        if (dim.id === "QUELLE") {
            sources = dim.domain.map((s, i) => {
                s.id = s.id === undefined ? 'URL ' + i : s.id,
                    s.label = s.label === undefined ? s.id.split("/").slice(-1)[0] : s.label;
                return {
                    text: s.label,
                    url: s.id
                };
            });
        }
    });
    let glossarItems = [
        {
            text: "Es sind keine Glossar-Einträge verfügbar.",
            url: "#"
        }
    ];
    dimensions.forEach((dim) => {
        if (dim.id === "GLOSSAR") {
            glossarItems = dim.domain.map((d, i) => {
                return {
                    text: d.label !== undefined ? d.label : "Beschreibung " + i,
                    url: ""
                };
            });
        }
    });
    let updateHistory = { items: [], url: "#" };
    Object.entries(defaultSlice.shapesGraph).forEach(([key, val]) => key === 'lastUpdate' ? updateHistory.items.push({ text: 'Letzte Aktualisierung', date: val['@value'] }) :
        key === 'nextUpdate' ? updateHistory.items.push({ text: 'Geplante Aktualisierung', date: val['@value'] }) :
            null);
    let collectingProcess = "";
    dimensionsWithMeasure.filter((dim) => dim.scopeNote !== undefined ?
        dimensionsWithMeasure.filter((dim) => dim.scopeNote)
            .map((g) => {
            collectingProcess = g.scopeNote;
        })
        : collectingProcess = "Es ist keine Erhebungsmethode definiert.");
    const licence = {
        text: dataset.license !== undefined ? dataset.license : "License",
        url: "#"
    };
    let footnotes = [];
    dimensions.filter((dim) => dim.id === "FUSSNOTE")
        .map((g) => {
        footnotes = g.domain.map((f, i) => {
            return {
                id: f.id !== undefined ? f.id : "ID" + i,
                label: f.label !== undefined ? f.label : "Beschreibung" + i
            };
        });
    });
    return {
        id: defaultSlice["@id"],
        dataURI: dataset.data["@id"],
        measure,
        label,
        dimensions,
        facets,
        unit,
        downloadUrl,
        sources,
        glossarItems,
        updateHistory,
        footnotes,
        collectingProcess,
        licence
    };
};
export const fetchDataSet = (id) => fetchJSON({
    url: `${API_BASE_URL}/dataset/${id}`,
    decode: data => {
        const transformed = transformDataSet(data);
        const result = Dataset.decode(transformed);
        // console.log(PathReporter.report(result));
        return result;
    }
});



// WEBPACK FOOTER //
// ./src/api/dataset.ts